// Reusable Solution page used for /company-secretaries, /accountants, /corporate-law
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight, ArrowUpRight, Sparkles, BookOpen, ShieldCheck } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { FEATURE_MODULES, STATS } from "@/constants/siteData";
import { SID } from "@/constants/siteIds";

export default function SolutionPage({
  eyebrow,
  title,
  highlight,
  subhead,
  intro,
  heroImage,
  featureKeys,
  benefits,
  faqs,
  longCopy,
  ctaTitle = "Ready to automate your company secretarial workload?",
}) {
  const features = featureKeys
    .map((k) => FEATURE_MODULES.find((f) => f.key === k))
    .filter(Boolean);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="dark-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />

        <div className="container-cv relative pt-14 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> {eyebrow}
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-primary leading-[1.04]">
              {title}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-accent">{highlight}</span>
                <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
              </span>
            </h1>
            <p className="mt-5 font-display text-lg md:text-xl font-semibold text-primary/80 leading-snug max-w-2xl">
              {subhead}
            </p>
            <p className="mt-5 text-base md:text-[17px] text-muted-foreground leading-relaxed max-w-2xl">
              {intro}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild data-testid={SID.solutionHeroBookDemo} className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
                <a href="/contact">
                  Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild data-testid={SID.solutionHeroSeePricing} variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-[hsl(207_74%_21%)] font-semibold bg-white/[0.04]">
                <Link to="/pricing">See Pricing</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-accent/20 via-transparent to-primary/10 rounded-3xl blur-2xl" />
              <div className="light-surface relative rounded-3xl border border-border bg-white p-6 overflow-hidden">
                <img
                  src={heroImage}
                  alt={title}
                  className="rounded-2xl h-72 w-full object-cover"
                />
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {STATS.slice(0, 3).map((s, i) => (
                    <div key={i} className="rounded-md bg-secondary/60 p-2.5 text-center">
                      <div className="font-display text-base font-bold text-primary leading-tight">{s.k}</div>
                      <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits strip */}
      {benefits?.length > 0 && (
        <section className="border-y border-border bg-secondary/40">
          <div className="container-cv py-10 md:py-14">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="lime-tick mt-0.5"><CheckCircle2 className="h-4 w-4" /></span>
                  <div>
                    <div className="font-display font-bold text-primary text-sm">{b.t}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Long copy */}
      {longCopy?.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="container-cv grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-4">
              <div className="sticky top-28">
                <span className="section-eyebrow">In depth</span>
                <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold text-primary tracking-tight leading-tight">
                  Built for the real day-to-day work.
                </h2>
                <BookOpen className="mt-6 h-8 w-8 text-accent" />
              </div>
            </aside>
            <div className="lg:col-span-8 space-y-10">
              {longCopy.map((blk, i) => (
                <div key={i}>
                  <h3 className="font-display text-xl md:text-2xl font-bold text-primary tracking-tight">{blk.h}</h3>
                  {blk.p?.map((p, j) => (
                    <p key={j} className="mt-3 text-base text-muted-foreground leading-relaxed">{p}</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features grid */}
      <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
        <div className="container-cv">
          <div className="max-w-3xl">
            <span className="section-eyebrow">Modules</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
              Every core task,{" "}
              <span className="text-accent">automated.</span>
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <Link
                key={f.key}
                to={`/cosecapp#${f.key}`}
                data-testid={SID.solutionFeatureCard(f.key)}
                className="group rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary group-hover:bg-accent group-hover:text-primary transition-colors">
                    <f.Icon className="h-5 w-5" />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-primary tracking-tight">{f.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      {faqs?.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="container-cv max-w-4xl">
            <div className="text-center">
              <span className="section-eyebrow">FAQs</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
                Common questions
              </h2>
            </div>
            <div className="mt-10">
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} data-testid={SID.solutionFaq(i + 1)} className="rounded-xl border border-border bg-white px-5">
                    <AccordionTrigger className="text-left font-display font-bold text-primary hover:no-underline py-5">
                      <span className="flex items-baseline gap-3">
                        <span className="font-mono text-xs font-bold text-accent">{String(i + 1).padStart(2, "0")}</span>
                        <span>{f.q}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20 md:py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              <span className="h-px w-8 bg-accent" /> Get started
            </span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              {ctaTitle}
            </h2>
            <p className="mt-3 text-white/70 text-base md:text-lg max-w-2xl">
              Join UK Cosec, finance and legal teams using CoSec App to file with Companies House, run boards, and stay ahead of every statutory deadline.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
              <a href="/contact">
                Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-bold">
              <Link to="/contact">Talk to Sales</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

SolutionPage.defaultProps = { featureKeys: [], benefits: [], faqs: [], longCopy: [] };
